import { useState } from 'react';
import CampoDinheiro from '@/components/base/CampoDinheiro';
import CampoArea from '@/components/base/CampoArea';
import OpcaoSelecao from '@/components/base/OpcaoSelecao';
import type { TipoAjuste } from '../types';

export interface AjustePayload {
  tipo: TipoAjuste;
  valor_centavos: number;
  motivo_interno: string | null;
}

const TIPOS: { valor: TipoAjuste; label: string }[] = [
  { valor: 'desconto', label: 'Desconto' },
  { valor: 'isencao', label: 'Isenção do saldo' },
];

interface Props {
  saldoCentavos: number;
  aoSalvar: (payload: AjustePayload) => void;
  aoCancelar: () => void;
  salvando: boolean;
  /** Erro vindo do servidor (ex.: a gravação falhou). Aparece acima do botão. */
  erroExterno?: string | null;
  /** Chamado quando o usuário edita um campo, para limpar o erro do servidor. */
  aoLimparErro?: () => void;
}

export default function FormConcederAjuste({
  saldoCentavos,
  aoSalvar,
  aoCancelar,
  salvando,
  erroExterno,
  aoLimparErro,
}: Props) {
  const [tipo, setTipo] = useState<TipoAjuste>('desconto');
  const [valorCentavos, setValorCentavos] = useState(0);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const erroMostrado = erro || erroExterno || null;

  function limpar() {
    setErro(null);
    aoLimparErro?.();
  }

  function mudarTipo(t: TipoAjuste) {
    setTipo(t);
    setValorCentavos(t === 'isencao' ? saldoCentavos : 0);
    limpar();
  }

  function enviar() {
    if (valorCentavos <= 0) {
      setErro('Informe o valor a ser abatido.');
      return;
    }
    setErro(null);
    aoSalvar({
      tipo,
      valor_centavos: valorCentavos,
      motivo_interno: motivo.trim() || null,
    });
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/30 sm:items-center sm:p-6">
      <div className="animate-modal-pop max-h-[90vh] w-full overflow-y-auto rounded-t-lg bg-white sm:max-w-md sm:rounded-lg">
        <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-4">
          <h3 className="text-lg font-medium text-foreground-950">
            Conceder desconto ou isenção
          </h3>
          <button
            type="button"
            onClick={aoCancelar}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-secondary-500 hover:bg-background-100"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="space-y-5 px-5 py-5">
          <div>
            <span className="block text-base font-medium text-foreground-950">
              Tipo <span className="text-danger-600">*</span>
            </span>
            <div className="mt-1 space-y-1">
              {TIPOS.map((t) => (
                <OpcaoSelecao
                  key={t.valor}
                  tipo="radio"
                  checked={tipo === t.valor}
                  onChange={() => mudarTipo(t.valor)}
                >
                  {t.label}
                </OpcaoSelecao>
              ))}
            </div>
          </div>

          <CampoDinheiro
            id="ajuste-valor"
            name="ajuste_valor"
            label="Valor"
            help="Quanto será abatido do que a pessoa deve."
            centavos={valorCentavos}
            onChange={(c) => {
              setValorCentavos(c);
              limpar();
            }}
            required
          />

          <CampoArea
            id="ajuste-motivo"
            name="ajuste_motivo"
            label="Motivo interno"
            help="Só a organização vê este texto. O participante nunca vê."
            value={motivo}
            onChange={(v) => {
              setMotivo(v);
              limpar();
            }}
            maxLength={500}
            simples
          />
        </div>

        {erroMostrado && (
          <p className="px-5 pb-3 text-sm font-medium text-danger-600" role="alert">
            {erroMostrado}
          </p>
        )}

        <div className="flex gap-3 border-t border-secondary-100 px-5 py-4">
          <button
            type="button"
            onClick={aoCancelar}
            className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={enviar}
            disabled={salvando}
            className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
          >
            {salvando ? 'Concedendo…' : 'Conceder'}
          </button>
        </div>
      </div>
    </div>
  );
}